const resolveApiRoot = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined' && window.location) {
    const host = window.location.hostname;
    if (host === 'innovher.com' || host === 'www.innovher.com') {
      if (!envUrl || envUrl.includes('localhost') || envUrl.includes('127.0.0.1')) {
        return 'https://innovher.ideaindia.co.in/api';
      }
    }
  }
  return (envUrl || 'http://localhost:5001/api').replace(/\/$/, '');
};
const API_ROOT = resolveApiRoot();
const V1 = `${API_ROOT}/v1`;
const API_ORIGIN = API_ROOT.replace(/\/api$/, '');

function queryString(params = {}) {
  const clean = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  const qs = new URLSearchParams(clean).toString();
  return qs ? `?${qs}` : '';
}

export function isStoredFileUrl(value) {
  return typeof value === 'string' && (/^https?:\/\//i.test(value) || value.startsWith('/uploads/'));
}

/** Stored file URLs may be relative to the API server (e.g. `/uploads/v1/…`); make them absolute. */
export function resolveFileUrl(url) {
  if (!url || typeof url !== 'string') return url;
  if (/^(https?:|blob:|data:)/i.test(url)) return url;
  if (url.startsWith('/uploads/') || url.startsWith('/api/')) return `${API_ORIGIN}${url}`;
  return url;
}

function getToken() {
  return localStorage.getItem('innovher_token');
}

function getRefresh() {
  return localStorage.getItem('innovher_refresh');
}

function headers(isJson = true) {
  const h = {};
  if (isJson) h['Content-Type'] = 'application/json';
  const token = getToken();
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

function saveSession(data) {
  if (data.accessToken || data.token) {
    localStorage.setItem('innovher_token', data.accessToken || data.token);
  }
  if (data.refreshToken) {
    localStorage.setItem('innovher_refresh', data.refreshToken);
  }
}

let refreshPromise = null;

async function refreshSession() {
  const refreshToken = getRefresh();
  if (!refreshToken) throw new Error('No refresh token');
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const res = await fetch(`${V1}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Session expired');
      saveSession(data);
      return data;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function isAuthUrl(url) {
  return /\/auth\/(login|refresh|logout|forgot-password|reset-password)\b/.test(url);
}

async function request(url, options = {}, _retried = false) {
  const isForm = options.body instanceof FormData;
  const res = await fetch(url, {
    ...options,
    headers: { ...headers(!isForm), ...options.headers },
  });
  const data = await res.json().catch(() => ({}));

  if (res.status === 401 && !_retried && !isAuthUrl(url) && getRefresh()) {
    try {
      await refreshSession();
      return request(url, options, true);
    } catch {
      localStorage.removeItem('innovher_token');
      localStorage.removeItem('innovher_refresh');
      localStorage.removeItem('innovher_user');
    }
  }

  if (!res.ok) throw new Error(data.message || data.error || 'Request failed');
  return data;
}

export const portalApi = {
  base: V1,

  login: async (email, password) => {
    const data = await request(`${V1}/auth/login`, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    saveSession(data);
    return data;
  },

  refresh: async () => {
    const data = await refreshSession();
    return data;
  },

  logout: async () => {
    try {
      await request(`${V1}/auth/logout`, {
        method: 'POST',
        body: JSON.stringify({ refreshToken: getRefresh() }),
      });
    } catch {
      /* ignore */
    }
    localStorage.removeItem('innovher_token');
    localStorage.removeItem('innovher_refresh');
    localStorage.removeItem('innovher_user');
  },

  me: () => request(`${V1}/me`),
  patchMe: (body) => request(`${V1}/me`, { method: 'PATCH', body: JSON.stringify(body) }),

  changePassword: (currentPassword, newPassword) =>
    request(`${V1}/auth/change-password`, {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  forgotPassword: (email) =>
    request(`${V1}/auth/forgot-password`, { method: 'POST', body: JSON.stringify({ email }) }),

  resetPassword: (token, newPassword) =>
    request(`${V1}/auth/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    }),

  // Apply
  getApplyForm: () => request(`${V1}/apply/form`),
  submitApplication: (formData) =>
    request(`${V1}/apply/applications`, { method: 'POST', body: formData }),
  listApplications: (status) =>
    request(`${V1}/apply/applications${status ? `?status=${status}` : ''}`),
  getApplication: (id) => request(`${V1}/apply/applications/${id}`),
  underReviewApplication: (id) =>
    request(`${V1}/apply/applications/${id}/under-review`, { method: 'POST', body: '{}' }),
  acceptApplication: (id, note) =>
    request(`${V1}/apply/applications/${id}/accept`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),
  rejectApplication: (id, note) =>
    request(`${V1}/apply/applications/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),
  myApplications: () => request(`${V1}/apply/applications/mine`),
  getApplyBank: () => request(`${V1}/apply/stages`),
  createApplyStage: (body) =>
    request(`${V1}/apply/stages`, { method: 'POST', body: JSON.stringify(body) }),
  createApplyQuestion: (body) =>
    request(`${V1}/apply/questions`, { method: 'POST', body: JSON.stringify(body) }),
  createApplyDocReq: (body) =>
    request(`${V1}/apply/document-requirements`, { method: 'POST', body: JSON.stringify(body) }),
  deleteApplyQuestion: (id) => request(`${V1}/apply/questions/${id}`, { method: 'DELETE' }),
  updateApplyQuestion: (id, body) =>
    request(`${V1}/apply/questions/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  reorderApplyQuestions: (ids) =>
    request(`${V1}/apply/questions/reorder`, { method: 'POST', body: JSON.stringify({ ids }) }),
  deleteApplyStage: (id) => request(`${V1}/apply/stages/${id}`, { method: 'DELETE' }),
  deleteApplyDocReq: (id) =>
    request(`${V1}/apply/document-requirements/${id}`, { method: 'DELETE' }),
  getApplicationDocuments: (id, params = {}) =>
    request(`${V1}/apply/applications/${id}/documents${queryString(params)}`),
  uploadApplicationDocuments: (id, formData) =>
    request(`${V1}/apply/applications/${id}/documents`, { method: 'POST', body: formData }),

  // Onboarding
  onboardingQueue: () => request(`${V1}/onboarding/startups`),
  onboardingBoard: (startupId) => request(`${V1}/onboarding/startups/${startupId}`),
  decideOnboarding: (startupId, body) =>
    request(`${V1}/onboarding/startups/${startupId}/decide`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  addOnboardingStep: (startupId, body) =>
    request(`${V1}/onboarding/startups/${startupId}/steps`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  reorderOnboardingSteps: (startupId, ids) =>
    request(`${V1}/onboarding/startups/${startupId}/steps/reorder`, {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),
  patchOnboardingStep: (stepId, body) =>
    request(`${V1}/onboarding/steps/${stepId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteOnboardingStep: (stepId) =>
    request(`${V1}/onboarding/steps/${stepId}`, { method: 'DELETE' }),
  moveOnboardingStep: (stepId, direction) =>
    request(`${V1}/onboarding/steps/${stepId}/move/${direction}`, {
      method: 'POST',
      body: '{}',
    }),
  completeStep: (stepId) =>
    request(`${V1}/onboarding/steps/${stepId}/complete`, { method: 'POST', body: '{}' }),
  setStepStatus: (stepId, status) =>
    request(`${V1}/onboarding/steps/${stepId}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),
  addMeeting: (stepId, body) =>
    request(`${V1}/onboarding/steps/${stepId}/meetings`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateMeeting: (meetingId, body) =>
    request(`${V1}/onboarding/meetings/${meetingId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteMeeting: (meetingId) =>
    request(`${V1}/onboarding/meetings/${meetingId}`, { method: 'DELETE' }),
  addComment: (stepId, body) =>
    request(`${V1}/onboarding/steps/${stepId}/comments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  addActionItem: (stepId, body) =>
    request(`${V1}/onboarding/steps/${stepId}/action-items`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  toggleActionItem: (id) =>
    request(`${V1}/onboarding/action-items/${id}/toggle`, { method: 'PATCH', body: '{}' }),
  deleteActionItem: (id) =>
    request(`${V1}/onboarding/action-items/${id}`, { method: 'DELETE' }),
  addDocRequest: (stepId, body) =>
    request(`${V1}/onboarding/steps/${stepId}/document-requests`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  reviewDocRequest: (id, status) =>
    request(`${V1}/onboarding/document-requests/${id}/review`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),
  sendOnboardingDoc: (stepId, formData) =>
    request(`${V1}/onboarding/steps/${stepId}/sent-documents`, {
      method: 'POST',
      body: formData,
    }),
  founderOnboarding: () => request(`${V1}/founder/onboarding`),
  markDocSeen: (id) =>
    request(`${V1}/founder/onboarding/sent-documents/${id}/seen`, {
      method: 'POST',
      body: '{}',
    }),
  downloadSentDocUrl: (id) => `${V1}/founder/onboarding/sent-documents/${id}/file`,
  submitDocRequest: (id, formData) =>
    request(`${V1}/founder/onboarding/document-requests/${id}/submit`, {
      method: 'POST',
      body: formData,
    }),

  // Batches
  listBatches: () => request(`${V1}/batches`),
  assignableBatches: () => request(`${V1}/batches/assignable`),
  getBatch: (id) => request(`${V1}/batches/${id}`),
  getBatchStartup: (batchId, startupId) =>
    request(`${V1}/batches/${batchId}/startups/${startupId}`),
  createBatch: (body) => request(`${V1}/batches`, { method: 'POST', body: JSON.stringify(body) }),
  patchBatch: (id, body) =>
    request(`${V1}/batches/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteBatch: (id, force = false) =>
    request(`${V1}/batches/${id}${force ? '?force=1' : ''}`, { method: 'DELETE' }),
  activateBatch: (id) => request(`${V1}/batches/${id}/activate`, { method: 'POST', body: '{}' }),
  closeBatch: (id) => request(`${V1}/batches/${id}/close`, { method: 'POST', body: '{}' }),
  listStartups: (params = {}) => request(`${V1}/batches/startups${queryString(params)}`),
  createStartup: (body) =>
    request(`${V1}/startups`, { method: 'POST', body: JSON.stringify(body) }),
  getStartup: (id) => request(`${V1}/startups/${id}`),
  updateStartup: (id, body) =>
    request(`${V1}/startups/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteStartup: (id, soft = false) =>
    request(`${V1}/startups/${id}${soft ? '?soft=1' : ''}`, { method: 'DELETE' }),

  // Company profile
  companyProfileForm: () => request(`${V1}/company-profile/form`),
  companyProfileBank: () => request(`${V1}/company-profile/stages`),
  createProfileStage: (body) =>
    request(`${V1}/company-profile/stages`, { method: 'POST', body: JSON.stringify(body) }),
  createProfileQuestion: (body) =>
    request(`${V1}/company-profile/questions`, { method: 'POST', body: JSON.stringify(body) }),
  createProfileDocReq: (body) =>
    request(`${V1}/company-profile/document-requirements`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteProfileStage: (id) =>
    request(`${V1}/company-profile/stages/${id}`, { method: 'DELETE' }),
  deleteProfileQuestion: (id) =>
    request(`${V1}/company-profile/questions/${id}`, { method: 'DELETE' }),
  updateProfileQuestion: (id, body) =>
    request(`${V1}/company-profile/questions/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  reorderProfileQuestions: (ids) =>
    request(`${V1}/company-profile/questions/reorder`, { method: 'POST', body: JSON.stringify({ ids }) }),
  deleteProfileDocReq: (id) =>
    request(`${V1}/company-profile/document-requirements/${id}`, { method: 'DELETE' }),
  myCompanyProfile: () => request(`${V1}/company-profile/mine`),
  saveCompanyProfile: (formData) =>
    request(`${V1}/company-profile/mine`, { method: 'PUT', body: formData }),
  adminCompanyProfile: (startupId) =>
    request(`${V1}/company-profile/startups/${startupId}`),
  uploadLogo: (formData) =>
    request(`${V1}/startups/mine/logo`, { method: 'POST', body: formData }),
  myStartup: () => request(`${V1}/startups/mine`),

  // Due diligence
  ddForm: () => request(`${V1}/due-diligence/form`),
  createDdStage: (body) =>
    request(`${V1}/due-diligence/stages`, { method: 'POST', body: JSON.stringify(body) }),
  createDdQuestion: (body) =>
    request(`${V1}/due-diligence/questions`, { method: 'POST', body: JSON.stringify(body) }),
  createDdDocReq: (body) =>
    request(`${V1}/due-diligence/document-requirements`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  createDdPack: (body) =>
    request(`${V1}/due-diligence/packs`, { method: 'POST', body: JSON.stringify(body) }),
  deleteDdStage: (id) => request(`${V1}/due-diligence/stages/${id}`, { method: 'DELETE' }),
  deleteDdQuestion: (id) =>
    request(`${V1}/due-diligence/questions/${id}`, { method: 'DELETE' }),
  updateDdQuestion: (id, body) =>
    request(`${V1}/due-diligence/questions/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  reorderDdQuestions: (ids) =>
    request(`${V1}/due-diligence/questions/reorder`, { method: 'POST', body: JSON.stringify({ ids }) }),
  deleteDdDocReq: (id) =>
    request(`${V1}/due-diligence/document-requirements/${id}`, { method: 'DELETE' }),
  openDd: (startupId, body) =>
    request(`${V1}/due-diligence/startups/${startupId}/open`, {
      method: 'POST',
      body: JSON.stringify(body || {}),
    }),
  getDdStartup: (startupId) => request(`${V1}/due-diligence/startups/${startupId}`),
  setDdStatus: (startupId, status) =>
    request(`${V1}/due-diligence/startups/${startupId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  founderDd: () => request(`${V1}/due-diligence/founder/me`),
  submitDd: (formData) =>
    request(`${V1}/due-diligence/founder/me`, { method: 'POST', body: formData }),
  getStartupDocuments: (startupId, params = {}) =>
    request(`${V1}/startups/${startupId}/documents${queryString(params)}`),
  uploadStartupDocuments: (startupId, formData) =>
    request(`${V1}/startups/${startupId}/documents`, { method: 'POST', body: formData }),
  toggleStartupDocumentAccess: (startupId, docId, grant) =>
    request(`${V1}/startups/${startupId}/documents/${docId}/startup-access`, {
      method: 'POST',
      body: JSON.stringify({ grant }),
    }),
  bulkStartupDocumentAccess: (startupId, docIds, grant) =>
    request(`${V1}/startups/${startupId}/documents/bulk-startup-access`, {
      method: 'POST',
      body: JSON.stringify({ docIds, grant }),
    }),
  requestStartupDocument: (startupId, body) =>
    request(`${V1}/startups/${startupId}/documents/request`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Batch tasks
  myBatchTasks: () => request(`${V1}/batch-tasks/me`),
  myMeetings: () => request(`${V1}/batch-tasks/me/meetings`),
  // Prefer singular /startup/ (stable alias); /startups/ also supported by BE
  startupBatchTasks: (startupId) => request(`${V1}/batch-tasks/startup/${startupId}`),
  createBatchTask: (startupIdOrBody, body) => {
    if (typeof startupIdOrBody === 'object') {
      return request(`${V1}/batch-tasks`, {
        method: 'POST',
        body: JSON.stringify(startupIdOrBody),
      });
    }
    return request(`${V1}/batch-tasks`, {
      method: 'POST',
      body: JSON.stringify({ ...(body || {}), startupId: startupIdOrBody }),
    });
  },
  createBatchTaskLegacy: (body) =>
    request(`${V1}/batch-tasks`, { method: 'POST', body: JSON.stringify(body) }),
  createDocumentSentTask: (startupId, formData) => {
    if (!(formData instanceof FormData)) throw new Error('formData required');
    return request(`${V1}/batch-tasks/startup/${startupId}/document-sent`, {
      method: 'POST',
      body: formData,
    });
  },
  transitionTask: (id, status, reason, reviewFeedback) =>
    request(`${V1}/batch-tasks/${id}/transition`, {
      method: 'POST',
      body: JSON.stringify({ status, reason, reviewFeedback }),
    }),
  patchBatchTask: (id, body) =>
    request(`${V1}/batch-tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  updateBatchMeeting: (taskId, body) =>
    request(`${V1}/batch-tasks/${taskId}/meeting`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteBatchTask: (id) => request(`${V1}/batch-tasks/${id}`, { method: 'DELETE' }),
  reviewBatchDocRequest: (taskId, status, feedback) =>
    request(`${V1}/batch-tasks/${taskId}/document-request/review`, {
      method: 'POST',
      body: JSON.stringify({ status, feedback }),
    }),
  submitBatchDocRequest: (taskId, formData) =>
    request(`${V1}/batch-tasks/${taskId}/document-request/submit`, {
      method: 'POST',
      body: formData,
    }),
  downloadBatchSentDocUrl: (docId) => `${V1}/batch-tasks/sent-documents/${docId}/file`,
  getBatchTask: (id) => request(`${V1}/batch-tasks/${id}`),
  addBatchComment: (id, body) =>
    request(`${V1}/batch-tasks/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify(typeof body === 'string' ? { body } : body),
    }),
  addBatchChecklist: (id, title) =>
    request(`${V1}/batch-tasks/${id}/checklist`, {
      method: 'POST',
      body: JSON.stringify({ title }),
    }),
  patchBatchChecklist: (taskId, itemId, body) =>
    request(`${V1}/batch-tasks/${taskId}/checklist/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteBatchChecklist: (taskId, itemId) =>
    request(`${V1}/batch-tasks/${taskId}/checklist/${itemId}`, { method: 'DELETE' }),

  // Notifications & users
  notifications: () => request(`${V1}/notifications`),
  unreadCount: () => request(`${V1}/notifications/unread-count`),
  readNotification: (id) =>
    request(`${V1}/notifications/${id}/read`, { method: 'PATCH', body: '{}' }),
  readAllNotifications: () =>
    request(`${V1}/notifications/read-all`, { method: 'POST', body: '{}' }),
  listUsers: () => request(`${V1}/auth/users`),
  updateUser: (id, body) =>
    request(`${V1}/auth/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteUser: (id, hard = false) =>
    request(`${V1}/auth/users/${id}${hard ? '?hard=1' : ''}`, { method: 'DELETE' }),
  listInvites: () => request(`${V1}/auth/invites`),
  createInvite: (body) =>
    request(`${V1}/auth/invites`, { method: 'POST', body: JSON.stringify(body) }),
  getInvite: (token) => request(`${V1}/auth/invites/${token}`),
  acceptInvite: (token, body) =>
    request(`${V1}/auth/invites/${token}/accept`, { method: 'POST', body: JSON.stringify(body) }),
  revokeInvite: (id) =>
    request(`${V1}/auth/invites/${id}/revoke`, { method: 'POST', body: '{}' }),
  // Contacts / Inquiries (public submit + admin inbox)
  submitContact: (body) =>
    request(`${V1}/contacts`, { method: 'POST', body: JSON.stringify(body) }).catch(() =>
      request(`${API_ROOT}/contact`, { method: 'POST', body: JSON.stringify(body) })
    ),
  listContacts: () =>
    request(`${V1}/contacts`).catch(() => request(`${API_ROOT}/contacts`)),
  patchContact: (id, body) =>
    request(`${V1}/contacts/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteContact: (id) =>
    request(`${V1}/contacts/${id}`, { method: 'DELETE' }),

  setUserFeatures: (id, features) =>
    request(`${V1}/auth/users/${id}/features`, {
      method: 'PUT',
      body: JSON.stringify({ features }),
    }),

  // Staff Daily Tasks (Admin-to-Subadmin workflow)
  listEligibleSubadmins: () => request(`${V1}/daily-tasks/eligible-subadmins`),
  listManagedDailyTasks: (params = {}) => {
    const q = new URLSearchParams();
    if (params.subadminId) q.set('subadminId', params.subadminId);
    if (params.status) q.set('status', params.status);
    if (params.date) q.set('date', params.date);
    if (params.overdue) q.set('overdue', 'true');
    const qs = q.toString();
    return request(`${V1}/daily-tasks/managed${qs ? `?${qs}` : ''}`);
  },
  listMyDailyTasks: (params = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.date) q.set('date', params.date);
    if (params.overdue) q.set('overdue', 'true');
    const qs = q.toString();
    return request(`${V1}/daily-tasks/my${qs ? `?${qs}` : ''}`);
  },
  getDailyTask: (id) => request(`${V1}/daily-tasks/${id}`),
  createDailyTask: (body) =>
    request(`${V1}/daily-tasks`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateDailyTask: (id, body) =>
    request(`${V1}/daily-tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  updateDailyTaskProgress: (id, body) =>
    request(`${V1}/daily-tasks/${id}/progress`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteDailyTask: (id) =>
    request(`${V1}/daily-tasks/${id}`, {
      method: 'DELETE',
    }),

  // Mentor Portal APIs (PDF Phase 1)
  getMentorOverview: (params = {}) => {
    const clean = Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null && v !== ''));
    const qs = new URLSearchParams(clean).toString();
    return request(`${V1}/mentor/overview${qs ? `?${qs}` : ''}`);
  },
  getMentorStartups: (params = {}) => {
    const clean = Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null && v !== ''));
    const qs = new URLSearchParams(clean).toString();
    return request(`${V1}/mentor/startups${qs ? `?${qs}` : ''}`);
  },
  getMentorStartup: (id) => request(`${V1}/mentor/startups/${id}`),
  getMentorCalendar: (params = {}) => {
    const clean = Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null && v !== '' && v !== 'all'));
    const qs = new URLSearchParams(clean).toString();
    return request(`${V1}/mentor/calendar${qs ? `?${qs}` : ''}`);
  },
  createMentorCalendarItem: (body) =>
    request(`${V1}/mentor/calendar/items`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  patchMentorCalendarItem: (id, body) =>
    request(`${V1}/mentor/calendar/items/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteMentorCalendarItem: (id, type) =>
    request(`${V1}/mentor/calendar/items/${id}?type=${encodeURIComponent(type || '')}`, {
      method: 'DELETE',
    }),
  getMentorTasks: (params = {}) => {
    const clean = Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null && v !== ''));
    const qs = new URLSearchParams(clean).toString();
    return request(`${V1}/mentor/tasks${qs ? `?${qs}` : ''}`);
  },
  createMentorPersonalTask: (body) =>
    request(`${V1}/mentor/tasks`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateMentorTask: (id, body) =>
    request(`${V1}/mentor/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteMentorPersonalTask: (id) =>
    request(`${V1}/mentor/tasks/${id}`, {
      method: 'DELETE',
    }),
  updateMentorTaskStatus: (id, status, reason = '') =>
    request(`${V1}/mentor/tasks/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, reason }),
    }),
  getMentorSettings: () => request(`${V1}/mentor/settings`),
  updateMentorSettings: (body) =>
    request(`${V1}/mentor/settings`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  uploadMentorAvatar: (formData) =>
    request(`${V1}/mentor/settings/avatar`, {
      method: 'POST',
      body: formData,
    }),

  // Superadmin Mentor Controls
  getAdminMentors: () => request(`${V1}/mentor/admin/mentors`),
  getAdminMentorDetail: (mentorId) => request(`${V1}/mentor/admin/mentors/${mentorId}`),
  getAdminMentorActivity: (mentorId) => request(`${V1}/mentor/admin/mentors/${mentorId}/activity`),
  adminAssignMentorTask: (body) =>
    request(`${V1}/mentor/admin/tasks`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  adminAssignStartupToMentor: (mentorId, startupId) =>
    request(`${V1}/mentor/admin/mentors/${mentorId}/assign-startup`, {
      method: 'POST',
      body: JSON.stringify({ startupId }),
    }),
  adminRemoveStartupFromMentor: (mentorId, startupId) =>
    request(`${V1}/mentor/admin/mentors/${mentorId}/remove-startup`, {
      method: 'POST',
      body: JSON.stringify({ startupId }),
    }),
  adminRemoveMentorFromStartup: (startupId, mentorId) =>
    request(`${V1}/mentor/admin/startups/${startupId}/remove-mentor`, {
      method: 'POST',
      body: JSON.stringify({ mentorId }),
    }),
  getAdminMentorDocuments: (mentorId, params = {}) => {
    const clean = Object.fromEntries(
      Object.entries(params || {}).filter(([_, v]) => v != null && v !== '')
    );
    const qs = new URLSearchParams(clean).toString();
    return request(`${V1}/mentor/admin/mentors/${mentorId}/documents${qs ? `?${qs}` : ''}`);
  },
  adminShareDocumentWithMentor: (docId, mentorId, startupId = null) =>
    request(`${V1}/mentor/admin/documents/${docId}/share`, {
      method: 'POST',
      body: JSON.stringify({ mentorId, startupId: startupId || undefined }),
    }),
  adminRevokeDocumentFromMentor: (docId, mentorId, startupId = null) =>
    request(`${V1}/mentor/admin/documents/${docId}/revoke`, {
      method: 'POST',
      body: JSON.stringify({ mentorId, startupId: startupId || undefined }),
    }),
  getAdminMentorMeetings: (mentorId, params = {}) => {
    const clean = Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null && v !== ''));
    const qs = new URLSearchParams(clean).toString();
    return request(`${V1}/mentor/admin/mentors/${mentorId}/meetings${qs ? `?${qs}` : ''}`);
  },
  adminScheduleMeeting: (mentorId, body) =>
    request(`${V1}/mentor/admin/mentors/${mentorId}/meetings`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  adminUpdateMeeting: (mentorId, meetingId, body) =>
    request(`${V1}/mentor/admin/mentors/${mentorId}/meetings/${meetingId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  adminCancelMeeting: (mentorId, meetingId, reason = '') =>
    request(`${V1}/mentor/admin/mentors/${mentorId}/meetings/${meetingId}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    }),

  // Startup Workspace Portal Methods
  getStartupPortalStatus: () => request(`${V1}/startup-portal/me/status`),
  getStartupPortalOverview: () => request(`${V1}/startup-portal/overview`),
  getStartupPortalProfile: () => request(`${V1}/startup-portal/profile`),
  updateStartupPortalProfile: (body) =>
    request(`${V1}/startup-portal/profile`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  getStartupPortalVault: () => request(`${V1}/startup-portal/vault`),
  uploadStartupPortalDoc: (formData) =>
    request(`${V1}/startup-portal/vault/upload`, {
      method: 'POST',
      body: formData,
    }),
  getStartupPortalDocVersions: (docId) => request(`${V1}/startup-portal/vault/${docId}/versions`),
  reviewStartupPortalDocAdmin: (docId, body) =>
    request(`${V1}/startup-portal/vault/${docId}/admin-review`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getStartupPortalRoadmap: () => request(`${V1}/startup-portal/roadmap`),
  updateStartupPortalTaskStatus: (taskId, body) =>
    request(`${V1}/startup-portal/tasks/${taskId}/status`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  submitStartupPortalTaskDeliverable: (taskId, formData) =>
    request(`${V1}/startup-portal/tasks/${taskId}/deliverable`, {
      method: 'POST',
      body: formData,
    }),
  reviewStartupPortalTaskAdmin: (taskId, body) =>
    request(`${V1}/startup-portal/tasks/${taskId}/admin-review`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getStartupPortalCalendar: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`${V1}/startup-portal/calendar${qs ? `?${qs}` : ''}`);
  },
  getStartupPortalInvestorReadiness: () => request(`${V1}/startup-portal/investor-readiness`),
  updateStartupPortalFundingNeeds: (body) =>
    request(`${V1}/startup-portal/investor-readiness/funding-needs`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  addStartupPortalInvestorInteraction: (body) =>
    request(`${V1}/startup-portal/investor-readiness/interactions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteStartupPortalInvestorInteraction: (id) =>
    request(`${V1}/startup-portal/investor-readiness/interactions/${id}`, {
      method: 'DELETE',
    }),
  getStartupPortalMetrics: () => request(`${V1}/startup-portal/metrics`),
  submitStartupPortalMetrics: (body) =>
    request(`${V1}/startup-portal/metrics/submit`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getStartupPortalSupport: () => request(`${V1}/startup-portal/support`),
  submitStartupPortalTicket: (body) =>
    request(`${V1}/startup-portal/support/tickets`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  replyStartupPortalTicket: (ticketId, body) =>
    request(`${V1}/startup-portal/support/tickets/${ticketId}/reply`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getStartupPortalTeam: () => request(`${V1}/startup-portal/team`),
  inviteStartupPortalTeamMember: (body) =>
    request(`${V1}/startup-portal/team/invite`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  revokeStartupPortalTeamInvite: (inviteId) =>
    request(`${V1}/startup-portal/team/invitations/${inviteId}/revoke`, {
      method: 'POST',
    }),
  removeStartupPortalTeamMember: (memberId) =>
    request(`${V1}/startup-portal/team/members/${memberId}`, {
      method: 'DELETE',
    }),

  // Admin Startup Access Governance
  getAdminStartupAccessStatus: (startupId) => request(`${V1}/startup-portal/admin/startups/${startupId}/access-status`),
  adminApproveStartupAccess: (startupId, body = {}) =>
    request(`${V1}/startup-portal/admin/startups/${startupId}/approve-access`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  adminSuspendStartupAccess: (startupId, reason) =>
    request(`${V1}/startup-portal/admin/startups/${startupId}/suspend-access`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  adminReactivateStartupAccess: (startupId) =>
    request(`${V1}/startup-portal/admin/startups/${startupId}/reactivate-access`, {
      method: 'POST',
    }),
};

export default portalApi;
